import { useState, useEffect, type CSSProperties } from 'react'
import { Download, Trash2, FileText, ExternalLink, Eye, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useContentById,
  useDeleteContent,
  useDownloadContent,
  useContentPreview,
} from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { toDisplayDate } from '@/lib/utils'
import { explainError } from '@/lib/explain-error'

interface ContentDetailModalProps {
  contentId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ContentDetailModal({ contentId, open, onOpenChange }: ContentDetailModalProps) {
  const { toast } = useToast()
  const { data, isLoading, error } = useContentById(contentId || '')
  const { data: previewData, isLoading: previewLoading } = useContentPreview(contentId)
  const deleteContentMutation = useDeleteContent()
  const downloadContentMutation = useDownloadContent()

  const content = data?.data
  const preview = previewData

  // State for blob URL (used for PDF/image preview to handle authentication)
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [blobLoading, setBlobLoading] = useState(false)

  // Fetch file and create blob URL for PDF/image preview
  useEffect(() => {
    if (!contentId || !preview) return

    if (preview.type === 'pdf' || preview.type === 'image') {
      setBlobLoading(true)
      setPreviewBlobUrl(null)
      setPreviewError(null)

      downloadContentMutation
        .mutateAsync(contentId)
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          setPreviewBlobUrl(url)
        })
        .catch((err) => {
          console.error('Failed to load preview:', err)
          // Surface an inline error so users don't stare at "Preview not
          // available" (which implies the file type isn't previewable) when
          // the real problem is a failed fetch.
          const status = err?.response?.status
          const msg =
            status === 410
              ? 'This file is no longer available — please delete and re-upload.'
              : status === 404
                ? 'File not found on the server.'
                : status === 502 || status === 503
                  ? 'File temporarily unavailable — a storage error occurred. Please try again shortly or contact an administrator.'
                  : err?.response?.data?.error || err?.message || 'Failed to load preview.'
          setPreviewError(msg)
        })
        .finally(() => {
          setBlobLoading(false)
        })
    }

    // Cleanup blob URL on unmount or when contentId changes
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl)
      }
    }
  }, [contentId, preview?.type])

  const handleDelete = async () => {
    if (!content) return
    if (!confirm(`Are you sure you want to delete "${content.title}"?`)) return

    try {
      await deleteContentMutation.mutateAsync(content.id)
      toast({
        title: 'Success',
        description: 'File deleted successfully',
      })
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to delete file'),
        variant: 'destructive',
      })
    }
  }

  const handleDownload = async () => {
    if (!content) return

    try {
      const blob = await downloadContentMutation.mutateAsync(content.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = content.title
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast({
        title: 'Success',
        description: 'File downloaded successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to download file'),
        variant: 'destructive',
      })
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  // GRK-BRAND-6: file-type badge = categorical tint fill + Prussian text.
  const getFileTypeStyle = (type: string): CSSProperties => {
    const tint: Record<string, string> = {
      PDF: CATEGORICAL.raspberry.tint,
      PNG: CATEGORICAL.navy.tint,
      JPG: CATEGORICAL.navy.tint,
      JPEG: CATEGORICAL.navy.tint,
      WORD_X: CATEGORICAL.steel.tint,
      EXCEL_X: CATEGORICAL.evergreen.tint,
      SNOTE: CATEGORICAL.plum.tint,
      HTML: CATEGORICAL.clay.tint,
      ZIP: GRACKLE_COLORS.paleGrey,
    }
    return {
      backgroundColor: tint[type] || GRACKLE_COLORS.paleGrey,
      color: GRACKLE_COLORS.prussianBlue,
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        aria-describedby="content-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" aria-hidden="true" />
            {content?.title || 'File Details'}
          </DialogTitle>
          <DialogDescription id="content-description">
            Preview, download, and manage file details
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground" role="status" aria-live="polite">
            Loading file details...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-destructive" role="alert" aria-live="assertive">
            Error loading file: {explainError(error, 'Unknown error')}
          </div>
        ) : content ? (
          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="preview" className="flex items-center gap-1">
                <Eye className="h-3 w-3" aria-hidden="true" />
                Preview
              </TabsTrigger>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="links">
                Links ({content.linked_entities?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="preview" className="mt-4">
              {previewLoading || blobLoading ? (
                <div
                  className="flex items-center justify-center h-64 text-muted-foreground"
                  role="status"
                >
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                  Loading preview...
                </div>
              ) : preview?.type === 'pdf' && previewBlobUrl ? (
                <div className="space-y-3">
                  <iframe
                    src={previewBlobUrl}
                    className="w-full h-[500px] border rounded-md"
                    title={`Preview of ${content.title}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => previewBlobUrl && window.open(previewBlobUrl, '_blank')}
                    aria-label="Open in new tab"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                    Open in new tab
                  </Button>
                </div>
              ) : preview?.type === 'image' && previewBlobUrl ? (
                <div className="space-y-3">
                  <div className="flex justify-center bg-muted rounded-md p-4">
                    <img
                      src={previewBlobUrl}
                      alt={content.title}
                      className="max-w-full max-h-[500px] object-contain rounded"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => previewBlobUrl && window.open(previewBlobUrl, '_blank')}
                    aria-label="Open in new tab"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                    Open in new tab
                  </Button>
                </div>
              ) : preview?.type === 'text' ? (
                <div className="space-y-3">
                  <pre className="whitespace-pre-wrap bg-muted p-4 rounded-md text-sm max-h-[500px] overflow-y-auto font-mono">
                    {preview.content}
                  </pre>
                  {preview.truncated && (
                    <p className="text-xs text-muted-foreground">
                      Content truncated. Download to see full file.
                    </p>
                  )}
                </div>
              ) : previewError ? (
                <div
                  className="flex flex-col items-center justify-center h-64 text-destructive space-y-4"
                  role="alert"
                >
                  <FileText className="h-12 w-12" aria-hidden="true" />
                  <p className="text-center px-4">{previewError}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground space-y-4">
                  <FileText className="h-12 w-12" aria-hidden="true" />
                  <p>{preview?.message || 'Preview not available for this file type'}</p>
                  <Button onClick={handleDownload} disabled={downloadContentMutation.isPending}>
                    <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                    Download to view
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="details" className="space-y-6 mt-4">
              {/* File Information */}
              <div>
                <h3 className="text-sm font-medium mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  File Information
                </h3>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">File Type:</dt>
                    <dd>
                      <Badge style={getFileTypeStyle(content.file_type)}>{content.file_type}</Badge>
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">Size:</dt>
                    <dd className="text-sm font-medium">{formatFileSize(content.content_size)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">Version:</dt>
                    <dd className="text-sm font-medium">{content.version_number}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">Latest Version:</dt>
                    <dd>
                      <Badge variant={content.is_latest ? 'default' : 'secondary'}>
                        {content.is_latest ? 'Yes' : 'No'}
                      </Badge>
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Description */}
              {content.description && (
                <div>
                  <h3 className="text-sm font-medium mb-2 bg-muted px-3 py-1.5 rounded-sm">
                    Description
                  </h3>
                  <p className="text-sm text-muted-foreground">{content.description}</p>
                </div>
              )}

              {/* Owner & Dates */}
              <div>
                <h3 className="text-sm font-medium mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Ownership & History
                </h3>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">Owner:</dt>
                    <dd className="text-sm font-medium">{content.owner_name || 'Unknown'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">Created:</dt>
                    <dd className="text-sm font-medium">
                      {(
                        toDisplayDate(content.created_date) ?? new Date(content.created_date)
                      ).toLocaleString()}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">Last Modified:</dt>
                    <dd className="text-sm font-medium">
                      {(
                        toDisplayDate(content.last_modified_date) ??
                        new Date(content.last_modified_date)
                      ).toLocaleString()}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Text Preview */}
              {content.text_preview && (
                <div>
                  <h3 className="text-sm font-medium mb-2 bg-muted px-3 py-1.5 rounded-sm">
                    Preview
                  </h3>
                  <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md max-h-48 overflow-y-auto">
                    {content.text_preview}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleDownload}
                  disabled={downloadContentMutation.isPending}
                  aria-label="Download file"
                >
                  <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                  Download
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteContentMutation.isPending}
                  aria-label="Delete file"
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Delete
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="links" className="mt-4">
              {content.linked_entities && content.linked_entities.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium mb-3 bg-muted px-3 py-1.5 rounded-sm">
                    Linked To
                  </h3>
                  <div className="space-y-2">
                    {content.linked_entities.map((link) => (
                      <div
                        key={link.id}
                        className="flex items-center justify-between p-3 border rounded-md"
                      >
                        <div>
                          <div className="text-sm font-medium">
                            Entity ID: {link.linked_entity_id}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Visibility: {link.visibility || 'Not specified'}
                          </div>
                        </div>
                        <Badge variant="secondary">{link.share_type || 'Unknown'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground" role="status">
                  No linked entities
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
